import React from "react";
import { Box, Typography, Paper, Chip, Link, Grid, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useUser } from "./UserContext";
import { releases, type Release, type ReleaseStatus } from "../pages/releaseData";

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const Container = styled(Box)({
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "40px 24px",
  minHeight: "100vh",
  backgroundColor: "#000000",
  color: "#ffffff",
});

const ReleaseCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  backgroundColor: "#111111",
  border: "1px solid #333333",
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 30px rgba(255, 255, 255, 0.05)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 12px 40px rgba(255, 255, 255, 0.1)",
    borderColor: "#444444",
  },
}));

const StatusChip = styled(Chip)<{ status: ReleaseStatus }>(({ status }) => ({
  backgroundColor:
    status === "DEMO" ? "#4a5568" : status === "STABLE" ? "#2d5016" : "#5a3d1a",
  color: "#ffffff",
  fontWeight: 500,
  fontSize: "0.75rem",
}));

const SectionTitle = styled(Typography)({
  color: "#ffffff",
  fontWeight: 600,
  marginBottom: "12px",
  fontSize: "1rem",
});

const SectionContent = styled(Box)({
  color: "#b0b0b0",
  fontSize: "0.875rem",
  lineHeight: 1.6,
});

const StyledLink = styled(Link)({
  color: "#60a5fa",
  textDecoration: "none",
  "&:hover": {
    color: "#93c5fd",
    textDecoration: "underline",
  },
});

const ListItem = styled("li")({
  marginBottom: "6px",
});

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const Releases: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const isAdmin =
    user?.role === "admin" ||
    user?.role === "superadmin" ||
    user?.role === "super_admin";

  if (!isAdmin) {
    navigate("/chat");
    return null;
  }

  return (
    <Container>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginBottom: "24px",
        }}
      >
        <IconButton
          onClick={() => navigate("/chat")}
          sx={{
            color: "#ffffff",
            backgroundColor: "#111111",
            border: "1px solid #333333",
            "&:hover": {
              backgroundColor: "#1a1a1a",
              borderColor: "#444444",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 600,
            color: "#ffffff",
            fontSize: { xs: "1.75rem", md: "2.25rem" },
          }}
        >
          Release Notes
        </Typography>
      </Box>

      <Typography
        variant="body1"
        sx={{
          color: "#b0b0b0",
          marginBottom: "40px",
          fontSize: "1rem",
        }}
      >
        Questo spazio rappresenta il punto ufficiale di pubblicazione delle
        versioni dell'ambiente Pandora.
      </Typography>

      {/* Release Cards */}
      <Box>
        {releases.map((release) => (
          <ReleaseCard key={release.version}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  color: "#ffffff",
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                Release {release.version}
              </Typography>
              <StatusChip label={release.status} status={release.status} />
            </Box>

            {/* Date */}
            <Typography
              variant="body2"
              sx={{
                color: "#888888",
                marginBottom: "24px",
                fontSize: "0.875rem",
              }}
            >
              Data rilascio: {release.date}
            </Typography>

            {/* Summary */}
            {release.summary && (
              <SectionContent sx={{ marginBottom: "24px", fontStyle: "italic" }}>
                {release.summary}
              </SectionContent>
            )}

            {/* Environment Info */}
            <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
              <Grid item xs={12} md={6}>
                <SectionTitle>Ambiente</SectionTitle>
                <SectionContent>
                  <Box sx={{ marginBottom: "8px" }}>
                    Frontend:{" "}
                    <StyledLink
                      href={release.environment.frontend}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {release.environment.frontend}
                    </StyledLink>
                  </Box>
                  <Box>
                    Backend API:{" "}
                    <StyledLink
                      href={release.environment.backend}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {release.environment.backend}
                    </StyledLink>
                  </Box>
                </SectionContent>
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionTitle>Database</SectionTitle>
                <Box
                  component="ul"
                  sx={{
                    color: "#b0b0b0",
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                    listStyle: "disc",
                    paddingLeft: "20px",
                    margin: 0,
                  }}
                >
                  {release.environment.databases.map((db) => (
                    <ListItem key={db}>{db}</ListItem>
                  ))}
                </Box>
              </Grid>
            </Grid>

            {/* Feature Sections */}
            {release.sections.map((section, idx) => (
              <Box key={idx} sx={{ marginBottom: "24px" }}>
                <SectionTitle>{section.title}</SectionTitle>
                <Box
                  component="ul"
                  sx={{
                    color: "#b0b0b0",
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                    listStyle: "disc",
                    paddingLeft: "20px",
                    margin: 0,
                  }}
                >
                  {section.items.map((item, itemIdx) => (
                    <ListItem key={itemIdx}>{item}</ListItem>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Notes */}
            {release.notes && release.notes.length > 0 && (
              <Box>
                <SectionTitle>Note</SectionTitle>
                <Box
                  component="ul"
                  sx={{
                    color: "#b0b0b0",
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                    listStyle: "disc",
                    paddingLeft: "20px",
                    margin: 0,
                  }}
                >
                  {release.notes.map((note, noteIdx) => (
                    <ListItem key={noteIdx}>{note}</ListItem>
                  ))}
                </Box>
              </Box>
            )}
          </ReleaseCard>
        ))}
      </Box>
    </Container>
  );
};

export default Releases;